/**
 * ModernReservationsTab - Enhanced reservations management with modern design
 */
import React, { useState, useMemo, useEffect } from 'react';
import { 
  Users, Search, Filter, Plus, Pencil, Trash2, Calendar, Mail, Phone, 
  CheckCircle, Clock, XCircle, TrendingUp, Download, Eye 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import AdminCard from './AdminCard';
import AdminCardActions from './AdminCardActions';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAdmin } from './AdminHooks';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';

const formatDateTime = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return String(s);
  return d.toLocaleString('fr-BE', {
    timeZone: 'Europe/Brussels',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    confirmed: { label: 'Confirmé', icon: CheckCircle, className: 'bg-green-500/20 text-green-400 border-green-500/30' },
    pending: { label: 'En attente', icon: Clock, className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    cancelled: { label: 'Annulé', icon: XCircle, className: 'bg-red-500/20 text-red-400 border-red-500/30' }
  };

  const config = statusConfig[status] || { label: status, icon: Clock, className: 'bg-gray-500/20 text-gray-400' };
  const Icon = config.icon;

  return (
    <Badge className={cn("flex items-center gap-1", config.className)}>
      <Icon size={12} />
      {config.label}
    </Badge>
  );
};

const ReservationCard = ({ reservation, onEdit, onDelete }) => (
  <AdminCard>
    <CardHeader className="pb-3">
      <div className="flex gap-3 justify-between items-start">
        <div className="min-w-0">
          <div className="flex gap-2 items-center">
            <CardTitle className="text-lg text-white truncate">{reservation.name}</CardTitle>
            {reservation.eventVolume && (
              <Badge className="text-blue-300 bg-blue-600/20 border-blue-600/30">Vol.{reservation.eventVolume}</Badge>
            )}
            <StatusBadge status={reservation.status} />
          </div>
          <div className="flex flex-wrap gap-2 items-center mt-1 text-sm text-slate-400">
            <span className="inline-flex items-center">
              <Mail size={14} className="mr-1" />
              {reservation.email}
            </span>
            {reservation.phone && (
              <span className="inline-flex items-center">
                <span className="mx-1 text-slate-600">•</span>
                <Phone size={14} className="mr-1" />
                {reservation.phone}
              </span>
            )}
            {reservation.createdAt && (
              <span className="inline-flex items-center">
                <span className="mx-1 text-slate-600">•</span>
                <Clock size={14} className="mr-1 text-slate-400" />
                {formatDateTime(reservation.createdAt)}
              </span>
            )}
          </div>
        </div>
      </div>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
          <div className="flex gap-4 items-center">
            <div className="flex gap-2 items-center text-slate-200">
              <Users size={16} className="text-blue-400" />
              <span className="font-medium">{reservation.guests} personnes</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {(reservation.menuSelections && reservation.menuSelections.length)
              ? reservation.menuSelections.map((s, idx) => (
                  <Badge key={idx} className="bg-slate-700 text-slate-200 border-slate-600">
                    {s.name} × {s.quantity}
                  </Badge>
                ))
              : (
                <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                  {(reservation.menuChoice === 'meat' && 'Viande') ||
                   (reservation.menuChoice === 'vegetarian' && 'Végétarien') ||
                   (reservation.menuChoice || 'Standard')}
                </Badge>
              )}
          </div>
        </div>
        <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs tracking-wide uppercase text-slate-400">Total</div>
              <div className="mt-1 text-2xl font-bold text-white">{reservation.totalPrice}€</div>
              <div className="mt-2 text-xs text-slate-400">TVA incluse si applicable</div>
            </div>
          </div>
        </div>
      </div>
    </CardContent>
    <AdminCardActions>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onEdit(reservation)}
        className="text-slate-300 hover:text-white hover:bg-slate-700"
        title="Modifier"
      >
        <Pencil size={14} />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onDelete(reservation)}
        className="text-slate-300 hover:text-red-400 hover:bg-red-500/10"
        title="Supprimer"
      >
        <Trash2 size={14} />
      </Button>
    </AdminCardActions>
  </AdminCard>
);

const ModernReservationsTab = () => {
  const {
    reservations, loading, searchTerm, statusFilter, events, stats,
    setSearchTerm, setStatusFilter, openEditModal, 
    setIsAddModalOpen, setIsDeleteModalOpen, setSelectedReservation
  } = useAdmin();

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.matchMedia('(max-width: 768px)').matches; } catch { return false; }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const mq = window.matchMedia('(max-width: 768px)');
      const handler = (e) => setIsMobile(e.matches);
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else mq.onchange = handler;
      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', handler);
        else mq.onchange = null;
      };
    } catch {}
  }, []);
  const [viewMode, setViewMode] = useState('table');
  const [sortBy, setSortBy] = useState('vol_desc');
  const [volumeFilter, setVolumeFilter] = useState('all');

  const filteredReservations = useMemo(() => {
    if (!reservations) return [];
    
    const filtered = reservations.filter(reservation => {
      const matchesSearch = !searchTerm || 
        reservation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reservation.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || reservation.status === statusFilter;
      const matchesVolume = volumeFilter === 'all' || parseInt(reservation.eventVolume || 0) === parseInt(volumeFilter);
      return matchesSearch && matchesStatus;
    });
    const filteredByVol = filtered.filter(r => volumeFilter === 'all' || parseInt(r.eventVolume || 0) === parseInt(volumeFilter));
    const sorted = [...filteredByVol].sort((a, b) => {
      const av = parseInt(a.eventVolume || 0);
      const bv = parseInt(b.eventVolume || 0);
      if (sortBy === 'vol_asc') return av - bv;
      if (sortBy === 'vol_desc') return bv - av;
      return 0;
    });
    return sorted;
  }, [reservations, searchTerm, statusFilter, volumeFilter, sortBy]);

  const handleEdit = (reservation) => {
    openEditModal(reservation);
  };

  const handleDelete = (reservation) => {
    setSelectedReservation(reservation);
    setIsDeleteModalOpen(true);
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Nom', 'Email', 'Téléphone', 'Personnes', 'Menu', 'Prix Total', 'Statut'],
      ...filteredReservations.map(r => [
        r.name,
        r.email,
        r.phone || '',
        r.guests,
          (r.menuSelections && r.menuSelections.length) ? r.menuSelections.map(s => `${s.name}x${s.quantity}`).join(' | ') : 'Standard',
        r.totalPrice,
        r.status
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reservations.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Gestion des Réservations</h2>
            <p className="text-slate-400">
              {filteredReservations.length} réservation(s) trouvée(s)
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={exportToCSV}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              <Download size={16} className="mr-2" />
              Exporter
            </Button>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Plus size={16} className="mr-2" />
              Nouvelle Réservation
            </Button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 w-4 h-4 transform -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 text-white bg-slate-900 border-slate-700 placeholder-slate-400"
          />
        </div>
        <div className="flex flex-wrap gap-2 items-center sm:flex-nowrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-white">
              <Filter size={16} className="mr-2" />
              <SelectValue />
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">Status</SelectItem>
              <SelectItem value="confirmed" className="text-white">Confirmé</SelectItem>
              <SelectItem value="pending" className="text-white">En attente</SelectItem>
              <SelectItem value="cancelled" className="text-white">Annulé</SelectItem>
            </SelectContent>
            </SelectTrigger>
          </Select>
          <Select value={volumeFilter} onValueChange={setVolumeFilter}>
            <SelectTrigger className="w-[160px] bg-slate-900 border-slate-700 text-white">
              <Filter size={16} className="mr-2" />
              <SelectValue placeholder="Vol." />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">Édition</SelectItem>
              {Array.from(new Set((events || []).map(e => e.volume))).sort((a,b) => b-a).map(v => (
                <SelectItem key={v} value={String(v)} className="text-white">Vol.{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex rounded-lg border bg-slate-900 border-slate-700">
            <Button
              size="sm"
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              onClick={() => setViewMode('table')}
              className={cn(
                "rounded-r-none",
                viewMode === 'table' && "bg-blue-600 text-white"
              )}
            >
              Tableau
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              onClick={() => setViewMode('cards')}
              className={cn(
                "rounded-l-none",
                viewMode === 'cards' && "bg-blue-600 text-white"
              )}
            >
              Cartes
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-4 w-8 h-8 rounded-full border-b-2 border-blue-400 animate-spin"></div>
            <p className="text-slate-400">Chargement des réservations...</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {viewMode === 'table' ? (
            <Card className="overflow-hidden bg-slate-800/50 border-slate-700">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 hover:bg-slate-800/30">
                      <TableHead className="text-slate-300">Nom</TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">Vol</TableHead>
                      <TableHead className="border-l text-slate-300 border-slate-700">Email</TableHead>
                      <TableHead className="border-l text-slate-300 border-slate-700">Téléphone</TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">Menu</TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">Statut</TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">Personnes</TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReservations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-8 text-center text-slate-400">
                          Aucune réservation trouvée
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredReservations.map((reservation) => (
                        <TableRow 
                          key={reservation.id} 
                          className="transition-colors border-slate-700 hover:bg-slate-800/30"
                        >
                          <TableCell className="font-medium text-white">{reservation.name}</TableCell>
                          <TableCell className="text-center border-l border-slate-700">
                            {reservation.eventVolume ? (
                              <Badge className="text-blue-300 bg-blue-600/20 border-blue-600/30">
                                Vol.{reservation.eventVolume}
                              </Badge>
                            ) : (
                              <span className="text-slate-500">—</span>
                            )}
                          </TableCell>
                          <TableCell className="border-l text-slate-300 border-slate-700">{reservation.email}</TableCell>
                          <TableCell className="border-l text-slate-300 border-slate-700">{reservation.phone || '—'}</TableCell>
                          <TableCell className="text-center border-l text-slate-300 border-slate-700">
                            <div className="flex flex-wrap gap-1 justify-center">
                              {(reservation.menuSelections && reservation.menuSelections.length)
                                ? reservation.menuSelections.map((s, idx) => (
                                    <Badge key={idx} className="bg-slate-700 text-slate-200 border-slate-600">
                                      {s.name} × {s.quantity}
                                    </Badge>
                                  ))
                                : (
                                  <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                    {(reservation.menuChoice === 'meat' && 'Viande') ||
                                     (reservation.menuChoice === 'vegetarian' && 'Végétarien') ||
                                     (reservation.menuChoice || 'Standard')}
                                  </Badge>
                                )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center border-l border-slate-700">
                            <StatusBadge status={reservation.status} />
                          </TableCell>
                          <TableCell className="text-center text-white border-l border-slate-700">{reservation.guests}</TableCell>
 
                          <TableCell className="text-right border-l border-slate-700">
                            <div className="flex gap-2 justify-end">
                              {reservation.createdAt && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-slate-400 hover:text-white hover:bg-slate-700"
                                      >
                                        <Clock size={14} />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      Créée le {formatDateTime(reservation.createdAt)}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(reservation)}
                                className="text-slate-400 hover:text-white hover:bg-slate-700"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(reservation)}
                                className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filteredReservations.map((reservation) => (
                <ReservationCard
                  key={reservation.id}
                  reservation={reservation}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

ModernReservationsTab.propTypes = {};

export default ModernReservationsTab;
